/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx'
import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { AlertModal } from '../../../common/components/AlertModal'
import { changePassword, verifyChangePasswordToken } from '../core/_requests'

interface PasswordValidations {
  hasLength: boolean
  hasUppercase: boolean
  hasLowercase: boolean
  hasDigit: boolean
  hasSymbol: boolean
  hasMinCategories: boolean
  noNameOrEmail: boolean
}

const changePasswordSchema = Yup.object().shape({
  new_password: Yup.string()
    .trim()
    .required('Nueva contraseña es requerida')
    .min(12, 'La contraseña debe tener al menos 12 caracteres'),
  confirmation_password: Yup.string()
    .trim()
    .required('Confirmar contraseña es requerida')
    .oneOf([Yup.ref('new_password')], 'Las contraseñas no coinciden'),
})

const initialValues = {
  new_password: '',
  confirmation_password: '',
}

export function ChangePassword() {
  const [loading, setLoading] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [passwordValidations, setPasswordValidations] = useState<PasswordValidations>({
    hasLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasDigit: false,
    hasSymbol: false,
    hasMinCategories: false,
    noNameOrEmail: true,
  })
  const navigate = useNavigate()
  const { email, token, name, lastname } = useParams()

  // // Validar token al montar el componente
  useEffect(() => {
    const validateToken = async () => {
      if (!token || !email) {
        setShowErrorModal(true)
        return
      }

      try {
        const response = await verifyChangePasswordToken(email, token)
        if (!response.data.valid) {
          setShowErrorModal(true)
        }
      } catch (error: any) {
        setShowErrorModal(true)
      }
    }

    validateToken()
  }, [token, email])

  const formik = useFormik({
    initialValues,
    validationSchema: changePasswordSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      // Validar que cumpla con todas las reglas
      const categoriesCount = [
        passwordValidations.hasUppercase,
        passwordValidations.hasLowercase,
        passwordValidations.hasDigit,
        passwordValidations.hasSymbol,
      ].filter(Boolean).length

      if (!passwordValidations.hasLength) {
        setStatus('La contraseña debe tener al menos 12 caracteres')
        return
      }

      if (categoriesCount < 3) {
        setStatus(
          'La contraseña debe cumplir al menos 3 de 4 categorías (mayúsculas, minúsculas, dígitos, símbolos)'
        )
        return
      }

      if (!passwordValidations.noNameOrEmail) {
        setStatus(
          'La contraseña no puede contener tu nombre, apellido o partes de tu correo electrónico'
        )
        return
      }

      setLoading(true)
      try {
        await changePassword({
          email: email || '',
          new_password: values.new_password,
          confirmation_password: values.confirmation_password,
          token: token || '',
        })
        setLoading(false)
        setSubmitting(false)
        setShowSuccessModal(true)
      } catch (error: any) {
        // Si el error es relacionado con token inválido o no disponible
        const errorMessage = error?.response?.data?.message || ''
        const statusCode = error?.response?.status
        
        // Verificar si el mensaje indica un error de token/enlace
        const isTokenError =
          statusCode === 401 ||
          statusCode === 404 || statusCode === 401 ||
          errorMessage.toLowerCase().includes('expirado') 

        if (isTokenError) {
          setLoading(false)
          setSubmitting(false)
          setShowErrorModal(true)
        } else {
          // Para errores 400 u otros errores de validación, mostrar el mensaje del servidor
          setStatus(
            errorMessage ||
              'Ha ocurrido un error al actualizar la contraseña. Por favor, intenta nuevamente.'
          )
          setSubmitting(false)
          setLoading(false)
        }
      }
    },
  })

  const handleKeyDown = (e: any) => {
    if (e.keyCode === 32) {
      e.preventDefault() // Prevent input of space character
    }
  }

  useEffect(() => {
    const { new_password } = formik.values
    const passwordLower = new_password.toLowerCase()

    const hasLength = new_password.length >= 12
    const hasUppercase = /[A-Z]/.test(new_password)
    const hasLowercase = /[a-z]/.test(new_password)
    const hasDigit = /[0-9]/.test(new_password)
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>\[\]\\\/\-_+=~`]/.test(new_password)

    // Contar categorías cumplidas
    const categoriesCount = [hasUppercase, hasLowercase, hasDigit, hasSymbol].filter(Boolean).length
    const hasMinCategories = categoriesCount >= 3

    // Validar que no contenga partes del email, nombre o apellido
    let noEmailParts = true
    let noNameParts = true
    let noLastnameParts = true

    if (email) {
      const emailParts = email.split('@')[0].toLowerCase()
      // Validar que no contenga partes significativas del email (mínimo 3 caracteres)
      if (emailParts.length >= 3) {
        noEmailParts = !passwordLower.includes(emailParts)
      }
    }

    if (name) {
      const nameLower = name.toLowerCase().trim()
      // Validar que no contenga el nombre completo o partes significativas (mínimo 3 caracteres)
      if (nameLower.length >= 3) {
        noNameParts = !passwordLower.includes(nameLower)
        // También validar partes del nombre si tiene más de 3 caracteres
        if (nameLower.length > 3) {
          for (let i = 0; i <= nameLower.length - 3; i++) {
            const part = nameLower.substring(i, i + 3)
            if (passwordLower.includes(part)) {
              noNameParts = false
              break
            }
          }
        }
      }
    }

    if (lastname) {
      const lastnameLower = lastname.toLowerCase().trim()
      // Validar que no contenga el apellido completo o partes significativas (mínimo 3 caracteres)
      if (lastnameLower.length >= 3) {
        noLastnameParts = !passwordLower.includes(lastnameLower)
        // También validar partes del apellido si tiene más de 3 caracteres
        if (lastnameLower.length > 3) {
          for (let i = 0; i <= lastnameLower.length - 3; i++) {
            const part = lastnameLower.substring(i, i + 3)
            if (passwordLower.includes(part)) {
              noLastnameParts = false
              break
            }
          }
        }
      }
    }

    const noNameOrEmail = noEmailParts && noNameParts && noLastnameParts

    setPasswordValidations({
      hasLength,
      hasUppercase,
      hasLowercase,
      hasDigit,
      hasSymbol,
      hasMinCategories,
      noNameOrEmail,
    })
  }, [formik.values.new_password, email, name, lastname])

  // Limpiar el mensaje de error cuando todas las validaciones pasan
  useEffect(() => {
    const allValidationsPass =
      formik.isValid &&
      passwordValidations.hasLength &&
      passwordValidations.hasMinCategories &&
      passwordValidations.noNameOrEmail

    if (formik.status && allValidationsPass) {
      formik.setStatus(undefined)
    }
  }, [
    formik.isValid,
    passwordValidations.hasLength,
    passwordValidations.hasMinCategories,
    passwordValidations.noNameOrEmail,
  ])

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      style={{ fontFamily: 'sans-serif', lineHeight: '18px', letterSpacing: '2%' }}
    >
      <div className='text-end mb-2'>
        <img
          src='/media/logos/prudencialsafblue.svg'
          alt='logo'
          style={{ width: 'auto', height: '45px' }}
        />
      </div>

      <div className='mb-10'>
        <h1 className='fw-bolder mb-3 text-2xl text-blue-primary'>Crea una nueva contraseña</h1>
        <p className='text-muted fs-4 text-paragraph'>
          Por tu seguridad, es necesario que cambies la contraseña provisional que te
          proporcionamos.
        </p>
      </div>

      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      <div className='d-flex flex-column flex-lg-row gap-10'>
        {/* Columna izquierda - Requisitos */}
        <div className='flex-lg-row-auto w-lg-320px bg-light rounded p-8'>
          <label className='form-label fs-5  mb-4'>Asegúrate que la contraseña tenga:</label>
          <div className='d-flex flex-column gap-3'>
            <div className='d-flex align-items-center'>
              {passwordValidations.hasLength ? (
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#0269A6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width='12'
                    height='9'
                    viewBox='0 0 12 9'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M10.5 1L4.5 7L1.5 4'
                      stroke='white'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
              ) : (
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#9CA3AF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width='12'
                    height='9'
                    viewBox='0 0 12 9'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M10.5 1L4.5 7L1.5 4'
                      stroke='white'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
              )}
              <span style={{ marginLeft: 12, fontSize: '14px', color: '#374151' }}>
                Mínimo 12 caracteres
              </span>
            </div>
            <div className='d-flex align-items-center'>
              {passwordValidations.hasUppercase ? (
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#0269A6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width='12'
                    height='9'
                    viewBox='0 0 12 9'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M10.5 1L4.5 7L1.5 4'
                      stroke='white'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
              ) : (
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#9CA3AF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width='12'
                    height='9'
                    viewBox='0 0 12 9'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M10.5 1L4.5 7L1.5 4'
                      stroke='white'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
              )}
              <span style={{ marginLeft: 12, fontSize: '14px', color: '#374151' }}>
                Al menos una mayúscula
              </span>
            </div>
            <div className='d-flex align-items-center'>
              {passwordValidations.hasLowercase ? (
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#0269A6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width='12'
                    height='9'
                    viewBox='0 0 12 9'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M10.5 1L4.5 7L1.5 4'
                      stroke='white'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
              ) : (
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#9CA3AF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width='12'
                    height='9'
                    viewBox='0 0 12 9'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M10.5 1L4.5 7L1.5 4'
                      stroke='white'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
              )}
              <span style={{ marginLeft: 12, fontSize: '14px', color: '#374151' }}>
                Al menos una minúscula
              </span>
            </div>
            <div className='d-flex align-items-center'>
              {passwordValidations.hasDigit ? (
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#0269A6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width='12'
                    height='9'
                    viewBox='0 0 12 9'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M10.5 1L4.5 7L1.5 4'
                      stroke='white'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
              ) : (
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#9CA3AF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width='12'
                    height='9'
                    viewBox='0 0 12 9'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M10.5 1L4.5 7L1.5 4'
                      stroke='white'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
              )}
              <span style={{ marginLeft: 12, fontSize: '14px', color: '#374151' }}>
                Al menos un número
              </span>
            </div>
            <div className='d-flex align-items-center'>
              {passwordValidations.hasSymbol ? (
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#0269A6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width='12'
                    height='9'
                    viewBox='0 0 12 9'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M10.5 1L4.5 7L1.5 4'
                      stroke='white'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
              ) : (
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#9CA3AF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width='12'
                    height='9'
                    viewBox='0 0 12 9'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M10.5 1L4.5 7L1.5 4'
                      stroke='white'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
              )}
              <span style={{ marginLeft: 12, fontSize: '14px', color: '#374151' }}>
                Al menos un carácter especial
              </span>
            </div>
          </div>
        </div>

        {/* Columna derecha - Campos del formulario */}
        <div className='flex-lg-row-fluid'>
          {/* Nueva contraseña */}
          <div className='fv-row mb-6'>
            <label className='form-label fs-6 fw-bolder text-paragraph mb-2'>
              Nueva contraseña
            </label>
            <input
              type='password'
              autoComplete='off'
              onKeyDown={handleKeyDown}
              {...formik.getFieldProps('new_password')}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid': formik.touched.new_password && formik.errors.new_password,
              })}
            />
            {formik.touched.new_password && formik.errors.new_password && (
              <div className='fv-plugins-message-container'>
                <span className='text-danger' role='alert'>
                  {formik.errors.new_password}
                </span>
              </div>
            )}
          </div>

          {/* Confirmar nueva contraseña */}
          <div className='fv-row mb-10'>
            <label className='form-label fs-6 fw-bolder text-paragraph mb-2'>
              Confirmar nueva contraseña
            </label>
            <input
              type='password'
              autoComplete='off'
              onKeyDown={handleKeyDown}
              {...formik.getFieldProps('confirmation_password')}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid':
                  formik.touched.confirmation_password && formik.errors.confirmation_password,
              })}
            />
            {formik.touched.confirmation_password && formik.errors.confirmation_password && (
              <div className='fv-plugins-message-container'>
                <span className='text-danger' role='alert'>
                  {formik.errors.confirmation_password}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Botón de actualizar */}
      <div className='text-center m-5'>
        <button
          style={{ maxWidth: '320px' }}
          type='submit'
          className='btn btn-lg mb-3  bg-blue-primary w-100 mt-4 text-white'
          disabled={
            formik.isSubmitting ||
            !formik.isValid ||
            !passwordValidations.hasLength ||
            !passwordValidations.hasMinCategories ||
            !passwordValidations.noNameOrEmail
          }
        >
          {!loading && <span className='indicator-label text-sm'>Actualizar contraseña</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Actualizando...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <div>
          <a
            href='#'
            onClick={(e) => {
              e.preventDefault()
              navigate('/auth/login')
            }}
            className='text-sm fw-bolder text-blue-primary  text-decoration-none'
          >
            Ir al inicio de sesión
          </a>
        </div>
      </div>

      {/* Modal de éxito */}
      <AlertModal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false)
          navigate('/auth/login')
        }}
        icon='success'
        maxWidth={400}
        title='Contraseña actualizada'
        message='La contraseña se ha actualizado correctamente. Ahora serás redirigido para iniciar sesión.'
        buttonText='Ir al inicio de sesión'
        fullWidthButton={true}
        onConfirm={() => {
          setShowSuccessModal(false)
          navigate('/auth/login')
        }}
      />

      <AlertModal
        show={showErrorModal}
        onHide={() => {
          setShowErrorModal(false)
          navigate('/auth/login')
        }}
        icon='error'
        title='Enlace no disponible'
        message='Parece que el enlace que sigues no existe o ya no está disponible. Por favor, solicita uno nuevo.'
        buttonText='Aceptar'
        fullWidthButton={true}
        maxWidth={400}
        onConfirm={() => {
          setShowErrorModal(false)
          navigate('/auth/login')
        }}
      />
    </form>
  )
}
